/**
 * Sheet `task-sheet` — Anlegen **und** Ändern in einer Komponente (Spec 06a,
 * „Felder in Reihenfolge"/„Aktionen"): Beide Vorgänge teilen sich exakt
 * dieselben Felder in derselben Reihenfolge, nur `task-status` kommt beim
 * Ändern zusätzlich ganz oben dazu, und `task-save-and-next` gibt es nur
 * beim Anlegen. `ticket === null` heißt Anlegen, dieselbe Unterscheidung wie
 * `charge` in `ChargeSheet.tsx`.
 *
 * Das Objekt (`task-subject`) steht am Objekt als Text im Kopf und ist **nicht
 * wählbar** — angelegt wird aus dem Kontext (`ARCHITECTURE.md` 6.4). Beim
 * Ändern kommt der Text aus `ticket.subject` (kann maskiert sein, `labels.ts`
 * fängt das ab), beim Anlegen aus `subjectId`/`subjectLabel` — dem geöffneten
 * Objekt der aufrufenden Route.
 *
 * Seit Spec 45 gibt es einen zweiten Einstieg **ohne** festes Objekt: den
 * Kalender (`kal-add-menu` → `task-add`). Ist `subjectId` leer, ersetzt
 * `task-subject-picker` (`EntityPicker`, Bauform `duedate-subject-picker`) den
 * Objekttext (R12) — dasselbe Sheet, zwei Einstiege. **Fälligkeit ist
 * Pflicht** (Fachregel 2): `task-due-on` trägt `required` und ist beim Anlegen
 * mit `initialDueOn` vorbelegt (Tag der Zelle bzw. `range.today`); es gibt
 * keinen leeren Zustand mehr und kein `null` im Rumpf.
 */

import { useEffect, useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { centsToDecimalInput } from '@/lib/money'
import { subjectLabel as displaySubjectLabel, costCenterLabel } from '@/features/tasks/labels'
import { buildTaskCreateBody, buildTaskPatchBody } from '@/features/tasks/task-form'
import type { TaskFormValues } from '@/features/tasks/task-form'
import { useCreateTicket, useUpdateTicket } from '@/features/tasks/api'
import type { TicketRow } from '@/features/tasks/api'
import { AssigneeField } from '@/components/AssigneeField'
import { EntityPicker } from '@/components/EntityPicker'
import type { EntitySelection } from '@/components/EntityPicker'
import { TaskCostCenterField } from '@/features/tasks/TaskCostCenterField'
import type { CostCenterSelection } from '@/features/tasks/TaskCostCenterField'
import { TaskPriorityField } from '@/features/tasks/TaskPriorityField'
import { TaskStatusField } from '@/features/tasks/TaskStatusField'
import { FIELD_CLASS, FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface TaskSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Leer (`''`): kein festes Objekt, `task-subject-picker` übernimmt (Spec 45). */
  readonly subjectId: string
  readonly subjectLabel: string
  /** `null` für Anlegen, sonst die zu ändernde Aufgabe. */
  readonly ticket: TicketRow | null
  /** Vorbelegtes „Fällig am" beim Anlegen (Spec 45, Regel 10) — ignoriert
   * beim Ändern. Fehlt es, bleibt das Feld leer und `required`. */
  readonly initialDueOn?: string
}

const EMPTY_FORM: TaskFormValues = {
  title: '',
  body: '',
  priority: 'normal',
  assigneeId: '',
  dueOn: '',
  cost: '',
  costCenterId: '',
  status: 'open',
}

function deriveEditForm(ticket: TicketRow): TaskFormValues {
  return {
    title: ticket.title,
    body: ticket.body ?? '',
    priority: ticket.priority.value,
    assigneeId: ticket.assignee?.id ?? '',
    dueOn: ticket.due_on ?? '',
    cost: ticket.cost_cents === null ? '' : centsToDecimalInput(ticket.cost_cents),
    costCenterId: ticket.cost_center?.id ?? '',
    status: ticket.status.value,
  }
}

function deriveCostCenterSelection(ticket: TicketRow | null): CostCenterSelection | null {
  if (ticket?.cost_center === null || ticket?.cost_center === undefined) return null
  return { id: ticket.cost_center.id, label: costCenterLabel(ticket.cost_center) ?? '' }
}

export function TaskSheet({ open, onOpenChange, subjectId, subjectLabel, ticket, initialDueOn }: TaskSheetProps) {
  const isEdit = ticket !== null
  const hasFixedSubject = isEdit || subjectId !== ''
  const emptyForm: TaskFormValues = { ...EMPTY_FORM, dueOn: initialDueOn ?? EMPTY_FORM.dueOn }
  const createMutation = useCreateTicket()
  const updateMutation = useUpdateTicket()
  const titleRef = useRef<HTMLInputElement>(null)
  const subjectPickerRef = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState<TaskFormValues>(EMPTY_FORM)
  const [initialForm, setInitialForm] = useState<TaskFormValues>(EMPTY_FORM)
  const [costCenterSelection, setCostCenterSelection] = useState<CostCenterSelection | null>(null)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [pickedSubject, setPickedSubject] = useState<EntitySelection | null>(null)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dasselbe React-Muster wie
  // `ContractCreateSheet`/`BuildingSheet` („Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt). Ein offenes Sheet
  // wechselt nie von einer Aufgabe zur nächsten, ohne vorher zu schließen —
  // die Auslöser (`task-add`, `task-row-<id>`) liegen hinter demselben
  // Overlay, das dieses Sheet selbst aufspannt.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      const nextForm = ticket !== null ? deriveEditForm(ticket) : emptyForm
      setForm(nextForm)
      setInitialForm(nextForm)
      setCostCenterSelection(deriveCostCenterSelection(ticket))
      setIsMoreOpen(ticket !== null && (ticket.cost_cents !== null || ticket.cost_center !== null))
      setPickedSubject(null)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  // Alle Felder von `TaskFormValues` sind `string` (s. Kopf von
  // `task-form.ts`) — ein einziger Rückgabetyp genügt, kein Generikum nötig.
  function updateField(name: keyof TaskFormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    // Das Datum bleibt stehen: „noch eins" legt meist weitere Aufgaben für
    // denselben Tag an, und am Objekt gibt es keinen Kalendertag zum
    // Vorbelegen (Fachregel 2: Fälligkeit ist Pflicht).
    const nextForm = { ...emptyForm, dueOn: form.dueOn }
    setForm(nextForm)
    setInitialForm(nextForm)
    setCostCenterSelection(null)
    setIsMoreOpen(false)
    setPickedSubject(null)
    setFieldError(undefined)
    setGeneralError(undefined)
    if (hasFixedSubject) {
      titleRef.current?.focus()
    } else {
      subjectPickerRef.current?.focus()
    }
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm)
  // Fälligkeit ist Pflicht (Spec 45): Ohne Datum geht nichts hinaus, auch nicht
  // über ⌘↩ am `required` vorbei — der Server antwortet sonst 422.
  const canSubmit = isEdit
    ? isDirty && form.dueOn !== ''
    : form.title.trim() !== '' && form.dueOn !== '' && (hasFixedSubject || pickedSubject !== null)
  const isPending = createMutation.isPending || updateMutation.isPending

  async function submit(andAnother: boolean) {
    if (!canSubmit || isPending) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      // `isEdit` allein narrows `ticket` bereits auf `TicketRow` (TS'
      // „Aliased Conditions"-Analyse, `isEdit` ist wörtlich `ticket !==
      // null`) — ein zweites `ticket !== null` daneben wäre redundant.
      if (isEdit) {
        const patchBody = buildTaskPatchBody(form, initialForm)
        if (Object.keys(patchBody).length > 0) {
          await updateMutation.mutateAsync({
            ticketId: ticket.id,
            ifMatch: String(ticket.version),
            body: patchBody,
          })
        }
        onOpenChange(false)
        return
      }

      // `canSubmit` hat bereits geprüft, dass ein Objekt feststeht — fest
      // vorgegeben oder aus dem Picker gewählt.
      const resolvedSubjectId = hasFixedSubject ? subjectId : (pickedSubject?.id ?? '')
      await createMutation.mutateAsync({ body: buildTaskCreateBody(resolvedSubjectId, form) })
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  // `submit` ändert sich bei jedem Rendern (schließt über `form`); ein Ref
  // hält die jeweils aktuelle Fassung, ohne dass der Tastenlistener unten bei
  // jeder Formulareingabe neu an- und abgemeldet werden muss — dieselbe
  // Abwägung, die `react-hooks/exhaustive-deps` mit „in `useCallback`
  // einwickeln" vorschlägt, hier aber an einer Kette hinge (`submit` ruft
  // `resetForAnother`, das wieder von den Formular-Settern abhinge). Der
  // Sicherheits-Check (`canSubmit`/`isPending`) sitzt bewusst **in** `submit`
  // selbst (oben) — so gilt er für den Knopf **und** die Taste gleichermaßen,
  // nicht zweimal gepflegt.
  const submitRef = useRef(submit)
  useEffect(() => {
    submitRef.current = submit
  })

  // `Cmd/Ctrl+Enter` speichert (Spec 06a, „Tastatur") — einzig an diesem
  // Sheet gefordert, deshalb kein Muster aus einem anderen Sheet übernommen.
  // `Esc` schließt bereits über Radix' `Dialog` (`components/ui/sheet.tsx`),
  // ohne eigenen Code. `window`-Listener statt `onKeyDown` am `<form>`
  // (`jsx-a11y/no-noninteractive-element-interactions`: ein Formular ist
  // kein interaktives Element für Tastaturereignisse) — dieselbe Bauform wie
  // das `Esc`-Handling in `ConfirmationStrip.tsx`.
  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        void submitRef.current(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const headSubject = isEdit ? displaySubjectLabel(ticket.subject) : subjectLabel
  // `AssigneeField` braucht die Id des tatsächlichen Objekts, nicht die
  // des Aufrufers: Im Bereich *Aufgaben* (`routes/Aufgaben.tsx`) reicht der
  // Container gar keine feste `subjectId` durch (dort gibt es kein festes
  // Objekt, `canAdd={false}`) — beim Ändern kommt sie deshalb aus der
  // Aufgabe selbst.
  const assigneeSubjectId = isEdit ? ticket.subject.id : hasFixedSubject ? subjectId : (pickedSubject?.id ?? '')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          if (hasFixedSubject) {
            titleRef.current?.focus()
          } else {
            subjectPickerRef.current?.focus()
          }
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form
          data-testid="task-sheet"
          onSubmit={(event) => void handleSubmit(event)}
          className="flex h-full flex-col"
        >
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              {isEdit ? 'Aufgabe ändern' : 'Aufgabe anlegen'}
            </SheetTitle>
            {hasFixedSubject ? (
              <p data-testid="task-subject" className="text-label text-muted">
                {headSubject}
              </p>
            ) : (
              // Kein Objekt vorgegeben (Kalender, Spec 45 R12) — der Picker
              // ersetzt den Text an derselben Stelle und ist das erste,
              // fokussierte Feld (Bauform `duedate-subject-picker`).
              <EntityPicker
                ref={subjectPickerRef}
                testId="task-subject-picker"
                label="Hängt an"
                value={pickedSubject}
                onSelect={setPickedSubject}
                error={fieldError}
                errorPrefix="task"
                apiField="subject_id"
              />
            )}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {isEdit && (
              <TaskStatusField
                testId="task-status"
                errorPrefix="task"
                value={form.status}
                onChange={updateField('status')}
                error={fieldError}
              />
            )}

            <FormField
              ref={titleRef}
              label="Titel"
              testId="task-title"
              apiField="title"
              errorPrefix="task"
              required
              value={form.title}
              onChange={updateField('title')}
              error={fieldError}
            />

            <FormTextareaField
              label="Beschreibung"
              testId="task-body"
              apiField="body"
              errorPrefix="task"
              value={form.body}
              onChange={updateField('body')}
              error={fieldError}
            />

            <TaskPriorityField
              testId="task-priority"
              errorPrefix="task"
              value={form.priority}
              onChange={updateField('priority')}
              error={fieldError}
            />

            <AssigneeField
              subjectId={assigneeSubjectId}
              testId="task-assignee"
              label="Zuständig"
              apiField="assignee_id"
              errorPrefix="task"
              value={form.assigneeId}
              onChange={updateField('assigneeId')}
              error={fieldError}
            />

            <FormField
              label="Fällig am"
              testId="task-due-on"
              apiField="due_on"
              errorPrefix="task"
              type="date"
              required
              value={form.dueOn}
              onChange={updateField('dueOn')}
              error={fieldError}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="task-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <FormField
                  label="Kosten"
                  testId="task-cost"
                  apiField="cost_cents"
                  errorPrefix="task"
                  inputMode="decimal"
                  value={form.cost}
                  onChange={updateField('cost')}
                  error={fieldError}
                />

                <TaskCostCenterField
                  testId="task-cost-center"
                  errorPrefix="task"
                  value={costCenterSelection}
                  onSelect={(entity) => {
                    setCostCenterSelection(entity)
                    setForm((current) => ({ ...current, costCenterId: entity.id }))
                  }}
                  onClear={() => {
                    setCostCenterSelection(null)
                    setForm((current) => ({ ...current, costCenterId: '' }))
                  }}
                  error={fieldError}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="task-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="task-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              {!isEdit && (
                <button
                  type="button"
                  data-testid="task-save-and-next"
                  disabled={!canSubmit || isPending}
                  aria-busy={isPending}
                  onClick={() => void submit(true)}
                  className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
                >
                  Anlegen und noch eins
                </button>
              )}
              <button
                type="submit"
                data-testid="task-save"
                disabled={!canSubmit || isPending}
                aria-busy={isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                {isEdit ? 'Speichern' : 'Anlegen'}
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
